
import { applicantStore } from '@/store'
import BiometricImage from './BiometricImage.vue'
import { Component, Prop, Vue } from 'vue-property-decorator'
import { FALLBACK_LOCALE } from '@/commons/constants/locale'
import BiometricRequirements from './BiometricRequirements.vue'
import { PersonUtils } from '@/commons/utils/person'
import { ApplicationUtils } from '@/commons/utils/application'
import {
    validatorIds,
    BiometryValidationFieldName,
    ApplicationType
} from '@/commons/constants/application'

import {
    ImageConstraints,
    SignatureRequiredRules,
    PersonBiometryComponent,
    BiometricImageComponent,
    LocaleMessageWithParams
} from '@/commons/types'
import { ENVIRONMENT } from '@/commons/configuration'

@Component({
    components: {
        BiometricImage,
        BiometricRequirements
    }
})
export default class PersonBiometry extends Vue implements PersonBiometryComponent {
    @Prop() applicationType!: ApplicationType

    domValidatorIds = validatorIds
    photoValidationId = BiometryValidationFieldName.PHOTO
    signatureValidationId = BiometryValidationFieldName.SIGNATURE

    handleSaving (): void {
        (this.$refs.biometryImage as BiometricImageComponent).resetBiometry()
        if (this.isSignatureBlockVisible) {
            (this.$refs.biometrySignature as BiometricImageComponent).resetBiometry()
        }
    }

    get titleKey (): string {
        return this.isSignatureBlockVisible ? 'personBiometry.titleWithSignature' : 'personBiometry.titleWithoutSignature'
    }

    get personAge (): number {
        return PersonUtils.calculateAge(applicantStore.personCode)
    }

    get isSignatureBlockVisible (): boolean {
        return this.personAge > this.signatureRequiredRules.notRequiredUntilAge
    }

    get isSignatureRequired (): boolean {
        return this.personAge >= this.signatureRequiredRules.requiredFromAge
    }

    get signatureRequiredRules (): SignatureRequiredRules {
        return ENVIRONMENT.signatureRequiredRules
    }

    get photoConstraints (): ImageConstraints {
        return ENVIRONMENT.photoConstraints
    }

    get signatureConstraints (): ImageConstraints {
        return ENVIRONMENT.signatureConstraints
    }

    get isPhotoSelected (): boolean {
        return !!applicantStore.applicationBiometry.photo || !!applicantStore.biometry?.photo
    }

    get userSelectedPhoto (): string {
        return applicantStore.applicationBiometry.photo || ''
    }

    setUserSelectedPhoto (photo: string | null) {
        applicantStore.applicationBiometry.photo = photo
    }

    get userSelectedSignature (): string {
        return applicantStore.applicationBiometry.signature || ''
    }

    setUserSelectedSignature (signature: string | null) {
        applicantStore.applicationBiometry.signature = signature
    }

    get isFingerprintsBlockVisible (): boolean {
        const age = PersonUtils.calculateAge(applicantStore.personCode)
        return ApplicationUtils.isFingerprintsRequired(this.applicationType, age)
    }

    get fingerprintsCaptured (): string | null | undefined {
        return applicantStore.fingerprintsCaptured
    }

    get serverPhotoUrl (): string {
        return applicantStore.biometry?.photo ? applicantStore.biometry.photo.data : ''
    }

    get serverSignatureUrl (): string {
        return applicantStore.biometry?.signature ? applicantStore.biometry.signature.data : ''
    }

    get missingSignatureImage (): string {
        return `missingSignature_${this.$i18n.locale || FALLBACK_LOCALE}.png`
    }

    get signatureRequirements (): Array<LocaleMessageWithParams> {
        const requirements = [
            {
                templateKey: 'personBiometry.differentSignature'
            },
            {
                templateKey: 'personBiometry.signatureContrast'
            },
            {
                templateKey: 'personBiometry.monochromeSignature'
            },
            {
                templateKey: 'personBiometry.signatureFileSize',
                params: {
                    width: this.signatureConstraints.minWidth,
                    height: this.signatureConstraints.minHeight
                }
            }
        ]
        if (this.personAge >= this.signatureRequiredRules.optionalFromAge &&
            this.personAge <= this.signatureRequiredRules.optionalUntilAge) {
            requirements.push({
                templateKey: 'personBiometry.optionalSignature'
            })
        }
        return requirements
    }

    get photoRequirements (): Array<LocaleMessageWithParams> {
        return [
            {
                templateKey: 'personBiometry.photoFileAge'
            },
            {
                templateKey: 'personBiometry.photoFileSize',
                params: {
                    minSize: this.photoConstraints.minSizeText,
                    maxSize: this.photoConstraints.maxSizeText
                }
            },
            {
                templateKey: 'personBiometry.photoFileDimensions',
                params: {
                    width: this.photoConstraints.minWidth,
                    height: this.photoConstraints.minHeight
                }
            }
        ]
    }

    get fingerprintsAge (): number {
        return ENVIRONMENT.fingerprintConstraints.validityInYears
    }
}
